import React from "react";

export interface ServiceErrorProps {
  title?: string;
  message: string;
  type: "NETWORK" | "VALIDATION" | "API" | "PROCESSING" | "PERMISSION";
  onRetry?: () => void;
  onGoBack?: () => void;
  showRetry?: boolean;
  showGoBack?: boolean;
}

const ServiceErrorTemplate: React.FC<ServiceErrorProps> = ({ title, message, type, onRetry, onGoBack, showRetry = true, showGoBack = true }) => {
  const getErrorIcon = () => {
    switch (type) {
      case "NETWORK":
        return (
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        );
      case "VALIDATION":
        return (
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.348 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        );
      case "PERMISSION":
        return (
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
    }
  };

  const getErrorTitle = () => {
    if (title) return title;

    switch (type) {
      case "NETWORK":
        return "Connection Problem";
      case "VALIDATION":
        return "Invalid Input";
      case "API":
        return "Service Error";
      case "PROCESSING":
        return "Processing Failed";
      case "PERMISSION":
        return "Permission Required";
      default:
        return "An Error Occurred";
    }
  };

  const getBgColor = () => {
    switch (type) {
      case "VALIDATION":
        return "bg-yellow-100";
      case "PERMISSION":
        return "bg-orange-100";
      default:
        return "bg-red-100";
    }
  };

  return (
    <div className="flex flex-col items-center w-full gap-4">
      <div className="w-full max-w-md p-8 bg-white border border-gray-200 shadow-sm rounded-xl">
        <div className="flex flex-col items-center gap-6">
          <div className={`flex items-center justify-center w-16 h-16 rounded-full ${getBgColor()}`}>{getErrorIcon()}</div>

          <div className="text-center">
            <h3 className="mb-3 text-xl font-medium text-secondary">{getErrorTitle()}</h3>
            <p className="mb-2 text-gray-600">{message}</p>
          </div>

          <div className="flex w-full gap-3">
            {showRetry && onRetry && (
              <button onClick={onRetry} className="flex-1 px-4 py-3 font-medium transition-all duration-300 rounded-lg btn-primary hover:shadow-md">
                Try Again
              </button>
            )}
            {showGoBack && onGoBack && (
              <button onClick={onGoBack} className="flex-1 px-4 py-3 font-medium text-gray-600 transition-all duration-300 bg-gray-100 rounded-lg hover:bg-gray-200">
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceErrorTemplate;
